import { inject, Injectable, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { BackendService } from '@ipg/ngx-frontend-toolset';

export interface SupportSessionScope {
  unitId: string;
  unitName?: string;
  roleLabels: string[];
  permissions: string[];
}

export interface SupportSessionCode {
  id: string;
  scope: SupportSessionScope;
  status: 'active' | 'redeemed' | 'revoked';
  expired: boolean;
  createdAt: string;
  expiresAt: string;
  createdByUserId: string;
  createdByDisplayName?: string;
  redeemedAt?: string;
  revokedAt?: string;
}

/** A code that was actually redeemed — support is, or was, inside. */
export interface SupportSession {
  id: string;
  scope: SupportSessionScope;
  shadowUserId: string;
  crmDisplayName: string;
  reason: string;
  status: 'active' | 'ended' | 'expired';
  /** Active on paper but past its window; the backend closes it within a minute. */
  expired: boolean;
  createdAt: string;
  expiresAt: string;
  endedAt?: string;
  endedBy?: 'crm' | 'tenant' | 'expiry';
}

export interface SupportSessionCodeCreated {
  /** Returned once and never again — the backend only stores a hash. */
  code: string;
  codeRecord: SupportSessionCode;
}

@Injectable({ providedIn: 'root' })
export class SupportSessionService {
  private readonly _backend = inject(BackendService);
  private readonly _codes = signal<SupportSessionCode[]>([]);
  private readonly _sessions = signal<SupportSession[]>([]);

  readonly codes = this._codes.asReadonly();
  readonly sessions = this._sessions.asReadonly();

  getCodes(): Observable<SupportSessionCode[]> {
    return this._backend
      .get<SupportSessionCode[]>('/support-session-codes')
      .pipe(tap((codes) => this._codes.set(codes)));
  }

  createCode(data: {
    unitId: string;
    userRoleIds: string[];
    durationMinutes?: number;
  }): Observable<SupportSessionCodeCreated> {
    return this._backend
      .post<SupportSessionCodeCreated>('/support-session-codes', data)
      .pipe(
        tap((created) =>
          this._codes.update((codes) => [created.codeRecord, ...codes])
        )
      );
  }

  getSessions(): Observable<SupportSession[]> {
    return this._backend
      .get<SupportSession[]>('/support-sessions')
      .pipe(tap((sessions) => this._sessions.set(sessions)));
  }

  /**
   * Withdraw consent from a session that is already running.
   *
   * The backend takes the permissions away and deletes support's tokens, so
   * this takes effect immediately rather than at the end of the 24 hours.
   */
  endSession(sessionId: string): Observable<SupportSession> {
    return this._backend
      .delete<SupportSession>(`/support-sessions/${sessionId}`)
      .pipe(
        tap((ended) =>
          this._sessions.update((sessions) =>
            sessions.map((session) =>
              session.id === sessionId ? ended : session
            )
          )
        )
      );
  }

  revokeCode(codeId: string): Observable<SupportSessionCode> {
    return this._backend
      .delete<SupportSessionCode>(`/support-session-codes/${codeId}`)
      .pipe(
        tap((revoked) =>
          this._codes.update((codes) =>
            codes.map((code) => (code.id === codeId ? revoked : code))
          )
        )
      );
  }
}
